/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import React from 'react'
import { Alert, Spin, Tooltip } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import Text from '../../ts-components/Text'
import AudienceDonutChart from './AudienceDonutChart'

/**
 * @interface AudienceBreakdownData
 * @property {number} total_users - Total number of users in the audience
 * @property {number} following_users - Number of users following the entity
 * @property {number} player_users - Number of player users
 * @property {number} official_users - Number of official users
 * @property {number} team_official_users - Number of team official users
 * @property {number} league_admin_users - Number of league admin users
 * @property {number} videographer_users - Number of videographer users
 * @property {number} admin_users - Number of admin users
 * @property {number} owner_users - Number of owner users
 * @property {number} unique_users - Number of unique users
 */
export interface AudienceBreakdownData {
  total_users: number
  unique_users: number
  following_users: number
  player_users: number
  official_users: number
  team_official_users: number
  league_admin_users: number
  videographer_users: number
  admin_users: number
  owner_users: number
}

/**
 * @interface AudienceBreakdownSectionProps
 * @property {string | undefined} selectedEntityId - ID of the selected entity
 * @property {string | null} selectedEntityType - Type of the selected entity ('org', 'team', 'sched')
 * @property {AudienceBreakdownData | null} [audienceData] - Optional audience data to display
 * @property {() => void} [onCalculate] - Optional callback function to trigger calculation
 * @property {boolean} [isCalculating] - Optional calculating state indicator
 * @property {boolean} [hasCalculated] - Whether audience has been calculated for current entity
 */
interface AudienceBreakdownSectionProps {
  selectedEntityId: string | undefined
  selectedEntityType: string | null
  audienceData?: AudienceBreakdownData | null
  onCalculate?: () => void
  isCalculating?: boolean
  hasCalculated?: boolean
  error?: string | null
  isCalculateDisabled?: boolean
}

/**
 * Audience Breakdown Section component - displays detailed audience statistics
 *
 * This component shows detailed audience breakdown statistics when an entity is selected.
 * It fetches audience data from the API and displays it in a clean format.
 *
 * Key features:
 * - Fetches and displays detailed audience breakdown
 * - Handles loading states and API errors gracefully
 * - Updates automatically when entity selection changes
 * - Only renders when an entity is selected
 *
 * @param props - Component props as defined in AudienceBreakdownSectionProps
 * @returns JSX element representing the audience breakdown section
 */
const AudienceBreakdownSection: React.FC<AudienceBreakdownSectionProps> =
  React.memo(
    ({
      selectedEntityId,
      selectedEntityType,
      audienceData = null,
      onCalculate,
      isCalculating = false,
      hasCalculated = false,
      error = null,
      isCalculateDisabled = false,
    }) => {
      // Always render the container, but show "No audience data available" if no entity is selected
      const hasSelectedEntity = selectedEntityId && selectedEntityType

      return (
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 12px;
            height: 100%;
          `}
        >
          {/* Header row with title, subtitle and button */}
          <div
            css={css`
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              gap: 16px;
            `}
          >
            <div
              css={css`
                flex: 1;
                display: flex;
                flex-direction: column;
                gap: 4px;
              `}
            >
              <Text
                variant='paragraphSmall'
                weight='medium'
                color={colors.WHITE}
              >
                Estimated Audience Size
              </Text>
              <Text
                variant='paragraphXSmall'
                weight='regular'
                color={colors.NEUTRAL_100}
              >
                {!hasSelectedEntity
                  ? 'Select an Entity to Calculate Audience'
                  : !audienceData
                  ? 'Click to Calculate Audience'
                  : ''}
              </Text>
            </div>

            {hasSelectedEntity && onCalculate && (
              <Tooltip
                title='Please select a target audience first'
                open={isCalculateDisabled ? undefined : false}
                zIndex={10002}
              >
                <button
                  type='button'
                  onClick={onCalculate}
                  disabled={isCalculating || isCalculateDisabled}
                  css={css`
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background-color: transparent;
                    border: 1px solid ${colors.PRIMARY};
                    border-radius: 6px;
                    cursor: ${isCalculating || isCalculateDisabled
                      ? 'not-allowed'
                      : 'pointer'};
                    opacity: ${isCalculating || isCalculateDisabled ? 0.5 : 1};
                    padding: 6px 12px;
                    gap: 8px;
                  `}
                  aria-label={hasCalculated ? 'Re-Calculate' : 'Calculate'}
                >
                  {isCalculating ? (
                    <Spin size='small' />
                  ) : (
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      height='20px'
                      viewBox='0 -960 960 960'
                      width='20px'
                      fill={colors.PRIMARY}
                    >
                      <path d='M0-240v-63q0-43 44-70t116-27q13 0 25 .5t23 2.5q-14 21-21 44t-7 48v65H0Zm240 0v-65q0-32 17.5-58.5T307-410q32-20 76.5-30t96.5-10q53 0 97.5 10t76.5 30q32 20 49 46.5t17 58.5v65H240Zm540 0v-65q0-26-6.5-49T754-397q11-2 22.5-2.5t23.5-.5q72 0 116 26.5t44 70.5v63H780Zm-455-80h311q-10-20-55.5-35T480-370q-55 0-100.5 15T325-320ZM160-440q-33 0-56.5-23.5T80-520q0-34 23.5-57t56.5-23q34 0 57 23t23 57q0 33-23 56.5T160-440Zm640 0q-33 0-56.5-23.5T720-520q0-34 23.5-57t56.5-23q34 0 57 23t23 57q0 33-23 56.5T800-440Zm-320-40q-50 0-85-35t-35-85q0-51 35-85.5t85-34.5q51 0 85.5 34.5T600-600q0 50-34.5 85T480-480Zm0-80q17 0 28.5-11.5T520-600q0-17-11.5-28.5T480-640q-17 0-28.5 11.5T440-600q0 17 11.5 28.5T480-560Zm1 240Zm-1-280Z' />
                    </svg>
                  )}
                  <Text
                    variant='paragraphSmall'
                    weight='medium'
                    color={colors.PRIMARY}
                  >
                    {hasCalculated ? 'Re-Calculate' : 'Calculate'}
                  </Text>
                </button>
              </Tooltip>
            )}
          </div>

          {/* Audience data section */}
          {hasSelectedEntity && (
            <AudienceDonutChart
              data={audienceData}
              hasCalculated={hasCalculated}
            />
          )}

          {error && (
            <Alert
              type='error'
              message={error}
              showIcon
              css={css`
                border-radius: 6px;
              `}
            />
          )}
        </div>
      )
    }
  )

export default AudienceBreakdownSection
